import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "../auth";
import { queryConfigs } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";

export type NotificationChannel = "in_app" | "push" | "email" | "whatsapp";

// Defaults opt-out: espelham notif_resolve_channels no banco.
export const CHANNEL_DEFAULTS: Record<NotificationChannel, boolean> = {
  in_app: true,
  push: true,
  email: false,
  whatsapp: false,
};

// Exceções por categoria (espelham o CASE de notif_resolve_channels, migration 20261008230000).
// Objetivos (OKR): o aviso de check-in é para o telefone, então WhatsApp nasce ligado.
export const CATEGORY_CHANNEL_DEFAULTS: Record<string, Partial<Record<NotificationChannel, boolean>>> = {
  objetivo: { whatsapp: true },
};

export interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
  tz: string;
}

export interface NotificationPreferences {
  user_id: string;
  channels: Record<string, Partial<Record<NotificationChannel, boolean>>>;
  quiet_hours: QuietHours;
  whatsapp_phone?: string;
  // colunas legadas (mantidas por compatibilidade)
  email_enabled?: boolean;
  push_enabled?: boolean;
}

export function useNotificationPreferences() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const userId = user?.user_id;

  // Key escopada por usuário: com staleTime Infinity (queryConfigs.session),
  // uma key global serviria as preferências do usuário anterior após troca de
  // conta/impersonação.
  const prefsKey = ["notification-preferences", userId];

  const { data: preferences, isLoading, error } = useQuery({
    ...queryConfigs.session,
    queryKey: prefsKey,
    enabled: !!userId,
    queryFn: async () => {
      const [prefsRes, profileRes] = await Promise.all([
        supabase.from("notification_preferences").select("*").eq("user_id", userId!).maybeSingle(),
        supabase.from("profiles").select("whatsapp_phone").eq("user_id", userId!).maybeSingle(),
      ]);

      if (prefsRes.error && prefsRes.error.code !== "PGRST116") throw prefsRes.error;
      const prefs = (prefsRes.data as unknown as NotificationPreferences) ?? ({} as NotificationPreferences);
      return { ...prefs, whatsapp_phone: profileRes.data?.whatsapp_phone ?? "" } as NotificationPreferences;
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!userId) throw new Error("User not authenticated");

      const { whatsapp_phone, ...prefsUpdates } = updates;

      if (Object.keys(prefsUpdates).length > 0) {
        const { error } = await supabase
          .from("notification_preferences")
          .upsert({ user_id: userId, ...prefsUpdates } as any);
        if (error) throw error;
      }

      if (whatsapp_phone !== undefined) {
        const digits = String(whatsapp_phone).replace(/\D/g, "");
        const { error } = await supabase
          .from("profiles")
          .update({ whatsapp_phone: digits || null })
          .eq("user_id", userId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prefsKey });
      toast({ title: "Pronto", description: "Preferências atualizadas" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Falha ao salvar preferências", variant: "destructive" });
    },
  });

  /** Valor efetivo de um canal numa categoria, considerando overrides + defaults. */
  const isChannelEnabled = (category: string, channel: NotificationChannel): boolean => {
    const override = preferences?.channels?.[category]?.[channel];
    return override ?? CATEGORY_CHANNEL_DEFAULTS[category]?.[channel] ?? CHANNEL_DEFAULTS[channel];
  };

  return {
    preferences,
    isLoading,
    error,
    isChannelEnabled,
    updatePreferences: updatePreferencesMutation.mutate,
    isUpdating: updatePreferencesMutation.isPending,
  };
}
